import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

import Footer from '../components/Footer';
import DynamicTitle from '../components/DynamicTitle';
import VideoCourse from '../components/VideoCourse';
import SEOMetaTags from '../components/SEOMetaTags';
export default function Vibe() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const {
    toast
  } = useToast();

  // Check for existing authentication on mount
  useEffect(() => {
    const authData = localStorage.getItem('vibeAuth');
    if (authData) {
      try {
        const { timestamp, userInfo } = JSON.parse(authData);
        const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
        
        if (Date.now() - timestamp < oneHour) {
          setIsAuthenticated(true);
          toast({
            title: "Բարի վերադարձ",
            description: `Բարի գալուստ, ${userInfo.display_name} ջան`
          });
        } else {
          localStorage.removeItem('vibeAuth');
        }
      } catch (error) {
        localStorage.removeItem('vibeAuth');
      }
    }
    setIsLoading(false);
  }, [toast]);

  // Simple device fingerprint to help limit sharing (no PII)
  const buildFingerprint = () => {
    try {
      const parts = [
        navigator.userAgent,
        navigator.language,
        String(window.screen?.width ?? ''),
        String(window.screen?.height ?? ''),
        String(new Date().getTimezoneOffset()),
      ];
      return parts.join('|');
    } catch {
      return 'unknown-device';
    }
  };

  // Keep session alive and detect replacement
  useEffect(() => {
    if (!isAuthenticated) return;

    const raw = localStorage.getItem('vibeSession');
    if (!raw) return;

    let parsed: { session_id: string; user_id: string; fingerprint: string } | null = null;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }
    if (!parsed?.session_id) return;

    const heartbeat = async () => {
      try {
        const { data } = await supabase.functions.invoke('heartbeat-course-session', {
          body: {
            session_id: parsed!.session_id,
            user_id: parsed!.user_id,
            device_fingerprint: parsed!.fingerprint,
          },
        });
        if (!data?.ok) {
          toast({
            title: 'Սեսիան փակվեց',
            description: 'Թվում ա՝ ուրիշ սարքից էս ակաունթով մտել են։',
            variant: 'destructive',
          });
          localStorage.removeItem('vibeSession');
          localStorage.removeItem('vibeAuth');
          setIsAuthenticated(false);
        }
      } catch {
        // Ignore transient errors
      }
    };

    // Start immediately then every 60s
    heartbeat();
    const interval = setInterval(heartbeat, 60_000);

    const onVisible = () => {
      if (document.visibilityState === 'visible') heartbeat();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isAuthenticated, toast]);
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const { data, error } = await supabase.rpc('verify_course_user', {
        user_email: credentials.email,
        user_password: credentials.password,
      });
      if (error) throw error;

      const result = data as any;
      if (result?.success) {
        // Store authentication data with timestamp
        const authData = {
          timestamp: Date.now(),
          userInfo: result,
        };
        localStorage.setItem('vibeAuth', JSON.stringify(authData));

        // Start a secure session (single active device)
        const fingerprint = buildFingerprint();
        try {
          const { data: sessionData } = await supabase.functions.invoke('start-course-session', {
            body: {
              user_id: result.id,
              device_fingerprint: fingerprint,
              user_agent: navigator.userAgent,
            },
          });
          if (sessionData?.session_id) {
            localStorage.setItem(
              'vibeSession',
              JSON.stringify({ session_id: sessionData.session_id, user_id: result.id, fingerprint })
            );
          }
        } catch (_) {
          // Ignore if session start fails; heartbeat will not run
        }

        setIsAuthenticated(true);
        toast({
          title: 'Պրիվետ',
          description: `Բարի գալուստ, ${result.display_name} ջան`,
        });
      } else {
        toast({
          title: 'Մի բան թարս գնաց',
          description: result?.error || 'Սխալ ա մեյլդ կամ գաղտնաբառդ:',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Մի բան թարս գնաց',
        description: 'Մուտքի ժամանակ սխալ ա տեղի ունեցել: Նորից փորձի էլի:',
        variant: 'destructive',
      });
    }
    setIsLoading(false);
  };
  const handleLogout = () => {
    localStorage.removeItem('vibeAuth');
    localStorage.removeItem('vibeSession');
    setIsAuthenticated(false);
    setCredentials({
      email: '',
      password: ''
    });
    toast({
      title: "Էս գնացի՞ր",
      description: "Լավ դե, մինչ վաղը:"
    });
  };
  if (isAuthenticated) {
    return <VideoCourse onLogout={handleLogout} />;
  }
  return <div className="min-h-screen flex flex-col bg-background">
      <SEOMetaTags 
        title="Vibe Coding Course - Learn Web Development Without Writing Code"
        description="Access the exclusive Vibe Coding course by Aram AI. Learn web development using AI tools without traditional programming. Login to start your journey."
        keywords="vibe coding course, AI web development, no-code programming, Armenian coding course, web development course, AI tools"
        canonicalUrl="https://tezzarida.io/vibe"
        ogType="website"
      />
      <DynamicTitle 
        title="Vibe Coding Course - Learn Web Development Without Writing Code"
        description="Access the exclusive Vibe Coding course by Aram AI. Learn web development using AI tools without traditional programming. Login to start your journey."
        keywords="vibe coding course, AI web development, no-code programming, Armenian coding course, web development course, AI tools"
        canonicalUrl="https://aramb.am/vibe"
      />
      
      
      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-card border-border">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-lg">V</span>
              </div>
            </div>
            <CardTitle className="text-xl sm:text-2xl font-heading text-foreground leading-tight">
              Բարի գալուստ «Վայբ Քոդինգ»<br className="block sm:hidden" />
              <span className="sm:hidden">Կուրսի Գլխավոր Դարպասներ</span>
              <span className="hidden sm:inline"> Կուրսի Գլխավոր Դարպասներ</span>
            </CardTitle>
            <p className="text-muted-foreground">
              Մուտքագրի տվյալներդ՝ փակ վիդեո կուրսը նայելու համար
            </p>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4" autoComplete="on">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-foreground">Մեյլ</Label>
                <Input 
                  id="email" 
                  name="email"
                  type="email" 
                  autoComplete="email"
                  placeholder="Մուտքագրի մեյլդ" 
                  value={credentials.email} 
                  onChange={e => setCredentials({
                    ...credentials,
                    email: e.target.value
                  })} 
                  required 
                  className="bg-background border-border text-foreground" 
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-foreground">Գաղտնաբառ</Label>
                <Input 
                  id="password" 
                  name="password"
                  type="password" 
                  autoComplete="current-password"
                  placeholder="Մուտքագրի գաղտնաբառդ" 
                  value={credentials.password} 
                  onChange={e => setCredentials({
                    ...credentials,
                    password: e.target.value
                  })} 
                  required 
                  className="bg-background border-border text-foreground" 
                />
              </div>
              
              <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" disabled={isLoading}>
                {isLoading ? 'Մուտք գործում...' : 'Մուտք դասընթաց'}
              </Button>
            </form>
            
            <div className="mt-6 p-4 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground text-center">Օգտագործի էն login ու password-ը, որը ստացել ես էն մեյլիդ, որը նշել ես վճարումը կատարելուց։</p>
            </div>
          </CardContent>
        </Card>
      </main>
      
      <Footer />
    </div>;
}